import * as Yup from 'yup';
import { v4 as uuidV4 } from 'uuid';
import { ISite } from './site';
import { ISiteFormValues } from './siteFormValues';
import { ISiteUpsert } from './siteUpsert';
import { IOrganization } from '../organization/organization';
import { IFtpDetail } from '../ftpDetail/ftpDetail';
import { IInspectionPoint } from '../inspectionPoint/inspectionPoint';
import { IContactInfo } from '../contactInfo/contactInfo';
import OptionType from '../../optionType';
import { IOptionType } from '@zz2/zz2-ui';
import { MAP_CENTER_POSITION } from '../../../../appConstants';
import { isNullOrWhiteSpace } from '../../../../services/appFunctionsService';

export default class SiteModelHelper {
    public static createFormValues = (
        site : ISite | null,
        organizations : Array<IOrganization>,
        contactInfos : Array<IContactInfo>,
        ftpDetails : Array<IFtpDetail>,
        inspectionPoints : Array<IInspectionPoint>,
    ) : ISiteFormValues => {
        if (!site) return {
            id: 0,
            guid: uuidV4(),
            code: '',
            description: '',
            shortDescription: '',
            latitude: MAP_CENTER_POSITION.lat,
            longitude: MAP_CENTER_POSITION.long,
            isPackhouse: false,
            isDeliveryPoint: false,
            isExternal: false,
            handlesExport: false,
            isBRCCompliant: false,
            isSimple: false,
            canMixSalesPoint: false,
            dispatchHeaderLogoImageBase64: '',
            inspectionPoint: null,
            globalGapNumber: '',
            packhouseDateCode: '',
            exporterName: '',
            exporterEmail: '',
            telNumber: '',
            address: '',
            faxNumber: '',
            organizations: [],
            contactInfos: [],
            ftpDetails: [],
            isActive: true,
        };

        const inspectionPoint = inspectionPoints.find(x => x.id === site.inspectionPointId);
        const organizationOptions = organizations
            .filter(x => site.organizationIds.includes(x.id))
            .map(OptionType.fromDataModel);
        const contactInfoOptions = contactInfos
            .filter(x => site.contactInfoIds.includes(x.id))
            .map(OptionType.fromDataModel);
        const ftpDetailOptions = ftpDetails
            .filter(x => site.ftpDetailIds.includes(x.id))
            .map(OptionType.fromDataModel);


        return {
            id: site.id,
            guid: site.guid,
            code: site.code,
            description: site.description,
            shortDescription: site.shortDescription,
            latitude: site.latitude,
            longitude: site.longitude,
            isPackhouse: site.isPackhouse,
            isDeliveryPoint: site.isDeliveryPoint,
            isExternal: site.isExternal,
            handlesExport: site.handlesExport,
            isBRCCompliant: site.isBRCCompliant,
            isSimple: site.isSimple,
            canMixSalesPoint: site.canMixSalesPoint,
            dispatchHeaderLogoImageBase64: '',
            inspectionPoint: inspectionPoint
                ? {
                    label: `( ${inspectionPoint.code} ) ${inspectionPoint.name}`, // TODO: OptionType.fromDataModel
                    value: inspectionPoint.id,
                }
                : null,
            globalGapNumber: site.globalGapNumber,
            packhouseDateCode: site.packhouseDateCode,
            exporterName: site.exporterName,
            exporterEmail: site.exporterEmail,
            telNumber: site.telNumber,
            address: site.address,
            faxNumber: site.faxNumber,
            organizations: organizationOptions,
            contactInfos: contactInfoOptions,
            ftpDetails: ftpDetailOptions,
            isActive: site.isActive,
        };
    };

    public static createUpsert = (formValues : ISiteFormValues) : ISiteUpsert => {
        return {
            id: formValues.id,
            guid: formValues.guid,
            code: formValues.code,
            description: formValues.description,
            shortDescription: formValues.shortDescription,
            latitude: formValues.latitude,
            longitude: formValues.longitude,
            isPackhouse: formValues.isPackhouse,
            isDeliveryPoint: formValues.isDeliveryPoint,
            isExternal: formValues.isExternal,
            handlesExport: formValues.handlesExport,
            isBRCCompliant: formValues.isBRCCompliant,
            isSimple: formValues.isSimple,
            canMixSalesPoint: formValues.canMixSalesPoint,
            dispatchHeaderLogoImageBase64: !isNullOrWhiteSpace(formValues.dispatchHeaderLogoImageBase64)
                ? formValues.dispatchHeaderLogoImageBase64
                : null,
            inspectionPointId: formValues.inspectionPoint
                ? Number(formValues.inspectionPoint.value)
                : null,
            globalGapNumber: !isNullOrWhiteSpace(formValues.globalGapNumber)
                ? formValues.globalGapNumber
                : null,
            packhouseDateCode: !isNullOrWhiteSpace(formValues.packhouseDateCode)
                ? formValues.packhouseDateCode
                : null,
            exporterName: !isNullOrWhiteSpace(formValues.exporterName)
                ? formValues.exporterName
                : null,
            exporterEmail: !isNullOrWhiteSpace(formValues.exporterEmail)
                ? formValues.exporterEmail
                : null,
            telNumber: !isNullOrWhiteSpace(formValues.telNumber)
                ? formValues.telNumber
                : null,
            address: !isNullOrWhiteSpace(formValues.address)
                ? formValues.address
                : null,
            faxNumber: !isNullOrWhiteSpace(formValues.faxNumber)
                ? formValues.faxNumber
                : null,
            organizationIds: formValues.organizations.map(x => Number(x.value)),
            contactInfoIds: formValues.contactInfos.map(x => Number(x.value)),
            ftpDetailIds: formValues.ftpDetails.map(x => Number(x.value)),
            isActive: formValues.isActive,
        };
    };

    public static formSchema = () : Yup.ObjectSchema<ISiteFormValues> => Yup.object().shape({
        id: Yup.number().typeError('Please enter a number.').required('Required'),
        guid: Yup.string().required('Required'),
        code: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        description: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        shortDescription: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        dispatchHeaderLogoImageBase64: Yup.string().trim().nullable().defined(),
        globalGapNumber: Yup.string().trim().nullable().defined(),
        packhouseDateCode: Yup.string().trim().nullable().defined(),
        exporterName: Yup.string().trim().nullable().defined(),
        exporterEmail: Yup.string().trim().nullable().defined(),
        telNumber: Yup.string().trim().nullable().defined(),
        address: Yup.string().trim().nullable().defined(),
        faxNumber: Yup.string().trim().nullable().defined(),
        latitude: Yup.number().typeError('Enter a number').defined().required('Required'),
        longitude: Yup.number().typeError('Enter a number').defined().required('Required'),
        inspectionPoint: Yup.mixed<IOptionType>().defined().nullable(),
        organizations: Yup.array<IOptionType>().min(1, 'At least one organization is required').defined().nullable().required('Required'),
        contactInfos: Yup.array<IOptionType>().defined().nullable().required('Required'),
        ftpDetails: Yup.array<IOptionType>().defined().nullable().required('Required'),
        isPackhouse: Yup.boolean().required('Required'),
        isDeliveryPoint: Yup.boolean().required('Required'),
        isExternal: Yup.boolean().required('Required'),
        handlesExport: Yup.boolean().required('Required'),
        isBRCCompliant: Yup.boolean().required('Required'),
        isSimple: Yup.boolean().required('Required'),
        canMixSalesPoint: Yup.boolean().required('Required'),
        isActive: Yup.boolean().required('Required'),
    });
}
